import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { IconGroup } from '@allbin/icons';
import { useSnackbar } from 'notistack';

import { DialogWindow } from '../DialogWindow';
import { ContractorForm } from './ContractorForm';
import ContractorDetails from './ContractorDetails';
import ConfirmDialog from '../ConfirmDialog';
import UnsavedChangesDialog from '../UnsavedChangesDialog';
import IconButton from '../core/IconButton';
import Button from '../core/Button';
import Permissions from '../auth/Permissions';
import { cn } from '../../helpers/classnames';
import {
  reviveContractor,
  useContractorMutations,
  useContractors,
} from '../../hooks/useContractors';
import { useFilterSets } from '../../hooks/useFilterSets';
import useUserStore from '../../store/user';
import { IconHelp, IconPlus } from '@/icons';
import useGuideStore from '@/store/guide';
import useContractorManagementDialogStore from '@/store/contractorManagementDialog';
import useConfigStore from '@/store/config';
import { allAuthUsersUrl } from '@/helpers/allauth_urls';

type PendingNavigation =
  | { type: 'close' }
  | { type: 'select'; contractor: Contractor }
  | { type: 'create' };

const messages = defineMessages({
  title: {
    id: 'contractor_management_dialog.title',
    defaultMessage: 'Contractors',
  },
  help: {
    id: 'help',
    defaultMessage: 'Help',
  },
  newContractor: {
    id: 'contractor_management_dialog.new_contractor',
    defaultMessage: 'New contractor',
  },
  editContractor: {
    id: 'contractor_management_dialog.edit_contractor',
    defaultMessage: 'Edit contractor',
  },
  contractorDetails: {
    id: 'contractor_management_dialog.contractor_details',
    defaultMessage: 'Contractor details',
  },
  selectPrompt: {
    id: 'contractor_management_dialog.select_or_create_prompt',
    defaultMessage:
      'Select a contractor to view its details, or create a new one.',
  },
  removeConfirm: {
    id: 'contractor_management_dialog.remove_confirm',
    defaultMessage:
      'This will permanently remove the contractor `{name}`. This cannot be undone.',
  },
});

const ContractorManagementDialog: FC = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { openGuide } = useGuideStore();
  const { show, setShow, selectedContractorId, setSelectedContractorId } =
    useContractorManagementDialogStore();
  const userManagementUrl = useConfigStore((s) => s.allauth.userManagementUrl);
  const userManagementHref = allAuthUsersUrl(userManagementUrl);

  const { list: contractors } = useContractors();
  // Fetched once at the dialog level (which stays mounted) so selecting a
  // contractor — and thereby remounting the keyed form — does not refetch.
  const { list: filtersets } = useFilterSets();
  const organizationId = useUserStore((s) => s.organizationId);
  const permissions = useUserStore((s) => s.permissions);
  const {
    create: { mutateAsync: createContractor, isLoading: isCreatingContractor },
    update: { mutateAsync: updateContractor, isLoading: isUpdatingContractor },
    remove: { mutate: removeContractor, isLoading: isRemovingContractor },
  } = useContractorMutations();

  const [selectedContractor, setSelectedContractor] = useState<
    Contractor | undefined
  >(undefined);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;

  const filteredAndSortedContractors = useMemo(() => {
    const filtered = permissions.includes('role:contractor-admin')
      ? contractors.filter((c) => c.id === organizationId)
      : contractors;
    return [...filtered].sort((a, b) => (a.name > b.name ? 1 : -1));
  }, [contractors, permissions, organizationId]);

  // Contractor-admins only ever see their own contractor, so auto-select it.
  useEffect(() => {
    if (
      permissions.includes('role:contractor-admin') &&
      filteredAndSortedContractors.length > 0
    ) {
      setSelectedContractor(filteredAndSortedContractors[0]);
    }
  }, [filteredAndSortedContractors, permissions]);

  const onClose = useCallback(() => {
    setShow(false);
    setSelectedContractorId(undefined);
    setSelectedContractor(undefined);
    setIsCreatingNew(false);
    setIsDirty(false);
    setPendingNavigation(null);
    setConfirmDeleteOpen(false);
  }, [setShow, setSelectedContractorId]);

  const applyNavigation = useCallback(
    (nav: PendingNavigation) => {
      setIsDirty(false);
      setPendingNavigation(null);
      if (nav.type === 'close') {
        onClose();
        return;
      }
      if (nav.type === 'create') {
        setSelectedContractor(undefined);
        setIsCreatingNew(true);
        setSelectedContractorId(undefined);
        return;
      }
      setSelectedContractor(nav.contractor);
      setIsCreatingNew(false);
      setSelectedContractorId(nav.contractor.id);
    },
    [onClose, setSelectedContractorId],
  );

  const requestNavigation = useCallback(
    (nav: PendingNavigation) => {
      if (
        nav.type === 'select' &&
        nav.contractor.id === selectedContractor?.id &&
        !isCreatingNew
      ) {
        return;
      }
      if (nav.type === 'create' && isCreatingNew) {
        return;
      }
      if (isDirtyRef.current) {
        setPendingNavigation(nav);
        return;
      }
      applyNavigation(nav);
    },
    [applyNavigation, isCreatingNew, selectedContractor],
  );

  // Honor a contractor preselected elsewhere (e.g. from a search result).
  useEffect(() => {
    if (!selectedContractorId) {
      return;
    }
    const contractor = filteredAndSortedContractors.find(
      (c) => c.id === selectedContractorId,
    );
    if (!contractor) {
      return;
    }
    requestNavigation({ type: 'select', contractor });
  }, [selectedContractorId, filteredAndSortedContractors, requestNavigation]);

  const tryClose = useCallback(() => {
    if (pendingNavigation || confirmDeleteOpen) {
      return;
    }
    requestNavigation({ type: 'close' });
  }, [confirmDeleteOpen, pendingNavigation, requestNavigation]);

  const onCancelPendingNavigation = useCallback(() => {
    setPendingNavigation(null);
    setSelectedContractorId(isCreatingNew ? undefined : selectedContractor?.id);
  }, [isCreatingNew, selectedContractor, setSelectedContractorId]);

  const onCreate = useCallback(
    (contractor: ContractorRequest) => {
      void createContractor(contractor).then((c) => {
        const created = reviveContractor(c);
        setSelectedContractor(created);
        setIsCreatingNew(false);
        setSelectedContractorId(created.id);
        setIsDirty(false);
        enqueueSnackbar(
          <FormattedMessage
            id="contractor_pane.contractor_created"
            defaultMessage="Contractor created"
          />,
          { variant: 'success' },
        );
      });
    },
    [createContractor, enqueueSnackbar, setSelectedContractorId],
  );

  const onUpdate = useCallback(
    (contractor: ContractorRequest) => {
      if (!selectedContractor) {
        return;
      }
      void updateContractor({ id: selectedContractor.id, contractor }).then(
        (c) => setSelectedContractor(reviveContractor(c)),
      );
    },
    [selectedContractor, updateContractor],
  );

  const onTryRemove = useCallback(() => {
    if (!selectedContractor) {
      return;
    }
    setConfirmDeleteOpen(true);
  }, [selectedContractor]);

  const onRemove = useCallback(() => {
    if (!selectedContractor) {
      return;
    }
    removeContractor(selectedContractor.id);
    setConfirmDeleteOpen(false);
    setSelectedContractor(undefined);
    setIsCreatingNew(false);
    setSelectedContractorId(undefined);
    setIsDirty(false);
  }, [removeContractor, selectedContractor, setSelectedContractorId]);

  const isLoading =
    isCreatingContractor || isUpdatingContractor || isRemovingContractor;
  const canUpdateContractor = permissions.includes('contractors:update');

  return (
    <>
      <ConfirmDialog
        open={confirmDeleteOpen}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={onRemove}
      >
        {selectedContractor ? (
          <FormattedMessage
            {...messages.removeConfirm}
            values={{ name: selectedContractor.name }}
          />
        ) : undefined}
      </ConfirmDialog>
      <UnsavedChangesDialog
        open={!!pendingNavigation}
        onCancel={onCancelPendingNavigation}
        onOk={() => {
          if (pendingNavigation) {
            applyNavigation(pendingNavigation);
          }
        }}
      />
      <DialogWindow
        open={show}
        onOpenChange={(isOpen) => !isOpen && tryClose()}
        title={intl.formatMessage(messages.title)}
        headerActions={
          <IconButton
            variant="ghost"
            icon={<IconHelp />}
            aria-label={intl.formatMessage(messages.help)}
            onClick={() => openGuide('contractorOverview')}
          />
        }
        content={
          <div className="flex h-fit flex-col gap-4">
            {/* Users are managed in AllAuth; the link stays hidden until the
              allauth-ui deployment URL is configured. */}
            {userManagementHref && (
              <Permissions oneOf={['role:admin', 'role:contractor-admin']}>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    className="gap-x-1"
                    onClick={() =>
                      window.open(userManagementHref, '_blank', 'noopener')
                    }
                  >
                    <IconGroup className="h-4 w-4" />
                    <FormattedMessage
                      id="contractor_overview.manage_users"
                      defaultMessage="Manage users"
                    />
                  </Button>
                </div>
              </Permissions>
            )}
            <div className="flex h-fit gap-4">
              <div className="flex w-1/2 flex-col gap-2">
                {/* The contractor directory is deliberately admin-only in the UI;
                  contractor-admins only ever see their own contractor, which is
                  auto-selected above. */}
                <Permissions has="role:admin">
                  <ul className="grow gap-1 overflow-auto">
                    {filteredAndSortedContractors.map((c) => {
                      const isSelected =
                        c.id === selectedContractor?.id && !isCreatingNew;
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() =>
                              requestNavigation({
                                type: 'select',
                                contractor: c,
                              })
                            }
                            aria-current={isSelected ? 'true' : undefined}
                            className={cn(
                              'w-full rounded-sm p-2 text-left transition hover:cursor-pointer',
                              isSelected
                                ? 'bg-workorder-500 text-contrast-workorder hover:bg-workorder-500'
                                : 'hover:bg-workorder-200',
                            )}
                          >
                            {c.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </Permissions>
                <Permissions has="contractors:create">
                  <Button
                    startIcon={<IconPlus />}
                    variant="filled"
                    className="mt-auto shrink-0"
                    disabled={isLoading || isCreatingNew}
                    onClick={() => requestNavigation({ type: 'create' })}
                  >
                    {intl.formatMessage(messages.newContractor)}
                  </Button>
                </Permissions>
              </div>
              <div className="flex min-h-[400px] grow flex-col rounded bg-workorder-100 p-4">
                {isCreatingNew || selectedContractor ? (
                  <>
                    <h3 className="mb-6 text-lg font-bold">
                      {isCreatingNew
                        ? intl.formatMessage(messages.newContractor)
                        : canUpdateContractor
                        ? intl.formatMessage(messages.editContractor)
                        : intl.formatMessage(messages.contractorDetails)}
                    </h3>
                    <Permissions
                      has={
                        isCreatingNew
                          ? 'contractors:create'
                          : 'contractors:update'
                      }
                      fallback={
                        selectedContractor && !isCreatingNew ? (
                          <ContractorDetails
                            contractor={selectedContractor}
                            filtersets={filtersets}
                          />
                        ) : undefined
                      }
                    >
                      <ContractorForm
                        key={isCreatingNew ? 'new' : selectedContractor?.id}
                        contractor={
                          isCreatingNew ? undefined : selectedContractor
                        }
                        filtersets={filtersets}
                        isLoading={isLoading}
                        onDirty={setIsDirty}
                        onRemove={isCreatingNew ? undefined : onTryRemove}
                        onSave={isCreatingNew ? onCreate : onUpdate}
                      />
                    </Permissions>
                  </>
                ) : (
                  <div className="flex grow items-center justify-center text-center text-text-800">
                    <FormattedMessage {...messages.selectPrompt} />
                  </div>
                )}
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

export default ContractorManagementDialog;
